/**
 * StagePieChart — SVG donut chart showing SFDC stage distribution.
 * Pure SVG; renders a donut with centre label, leader lines, and
 * external segment labels matching the Figma design.
 */

import { useMemo } from 'react';
import { SFDC_STAGE_COLOR } from '../../../utils/sfdcStage';

interface StageSlice {
  stage: string;
  count: number;
  color: string;
}

interface Props {
  stageCounts: Map<string, number>;
  pipelineTotal: number;
}

const SVG_W = 420;
const SVG_H = 280;
const CX = SVG_W / 2;
const CY = SVG_H / 2;
const OUTER_R = 95;
const INNER_R = 62;
const LABEL_R = OUTER_R + 10;
const LEADER_END_R = OUTER_R + 32;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export default function StagePieChart({ stageCounts, pipelineTotal }: Props) {
  const slices = useMemo<StageSlice[]>(() => {
    const out: StageSlice[] = [];
    for (const [stage, count] of stageCounts.entries()) {
      if (count > 0) {
        out.push({ stage, count, color: SFDC_STAGE_COLOR[stage] ?? '#94a3b8' });
      }
    }
    return out;
  }, [stageCounts]);

  const total = useMemo(() => slices.reduce((s, d) => s + d.count, 0), [slices]);

  const arcs = useMemo(() => {
    if (total === 0) return [];
    let cumAngle = 0;
    return slices.map((s) => {
      const sweep = (s.count / total) * 360;
      const startAngle = cumAngle;
      const endAngle = cumAngle + sweep;
      cumAngle = endAngle;
      const midAngle = startAngle + sweep / 2;
      return { ...s, startAngle, endAngle, midAngle };
    });
  }, [slices, total]);

  return (
    <svg width={SVG_W} height={SVG_H} viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="ivt-donut" style={{ overflow: 'visible' }}>
      {total === 0 ? (
        <circle cx={CX} cy={CY} r={OUTER_R} fill="none" stroke="#e2e8f0" strokeWidth={OUTER_R - INNER_R} />
      ) : (
        arcs.map((arc) => {
          const sweep = arc.endAngle - arc.startAngle;
          if (sweep >= 359.99) {
            return (
              <g key={arc.stage}>
                <circle cx={CX} cy={CY} r={(OUTER_R + INNER_R) / 2}
                  fill="none" stroke={arc.color} strokeWidth={OUTER_R - INNER_R} />
              </g>
            );
          }
          const outerStart = polarToCartesian(CX, CY, OUTER_R, arc.endAngle);
          const outerEnd = polarToCartesian(CX, CY, OUTER_R, arc.startAngle);
          const innerStart = polarToCartesian(CX, CY, INNER_R, arc.startAngle);
          const innerEnd = polarToCartesian(CX, CY, INNER_R, arc.endAngle);
          const largeArc = sweep > 180 ? 1 : 0;
          const d = [
            `M ${outerStart.x} ${outerStart.y}`,
            `A ${OUTER_R} ${OUTER_R} 0 ${largeArc} 0 ${outerEnd.x} ${outerEnd.y}`,
            `L ${innerStart.x} ${innerStart.y}`,
            `A ${INNER_R} ${INNER_R} 0 ${largeArc} 1 ${innerEnd.x} ${innerEnd.y}`,
            'Z',
          ].join(' ');
          return <path key={arc.stage} d={d} fill={arc.color} />;
        })
      )}

      {/* Leader lines + external segment labels */}
      {arcs.map((arc) => {
        const from = polarToCartesian(CX, CY, LABEL_R, arc.midAngle);
        const to = polarToCartesian(CX, CY, LEADER_END_R, arc.midAngle);
        const isRight = to.x >= CX;
        const lineEndX = isRight ? to.x + 16 : to.x - 16;
        const label = `${arc.stage} (${arc.count})`;
        return (
          <g key={`lbl-${arc.stage}`}>
            <polyline
              points={`${from.x},${from.y} ${to.x},${to.y} ${lineEndX},${to.y}`}
              fill="none"
              stroke="#9ca3af"
              strokeWidth={1}
            />
            <text
              x={isRight ? lineEndX + 4 : lineEndX - 4}
              y={to.y}
              fontSize={14}
              fontWeight={700}
              fill="#0f141a"
              textAnchor={isRight ? 'start' : 'end'}
              dominantBaseline="middle"
            >
              {label}
            </text>
          </g>
        );
      })}

      {/* Centre label */}
      <text x={CX} y={CY - 6} textAnchor="middle" fontSize={20} fontWeight={700} fill="#0f141a">
        {pipelineTotal}
      </text>
      <text x={CX} y={CY + 18} textAnchor="middle" fontSize={14} fill="#424650">
        Total Pipeline
      </text>
    </svg>
  );
}
